import { Plus, RotateCw, Trash2 } from "lucide-react";
import { useState } from "react";

import type { ManagedFile } from "@/components/file-uploader/types";

import { Button } from "@/components/button";
import { FileUploadTrigger } from "@/components/file-uploader/components/FileUploadTrigger";
import { getFileName } from "@/components/file-uploader/utils";

import type { AssetFileRef } from "./types";

import { useElementWidth } from "../../hooks/useElementWidth";
import { DocPlaceholder } from "../AssetThumb";
import { AllFilesModal } from "./AllFilesModal";
import { AssetFileTile } from "./AssetFileTile";
import { THUMB_FRAME_CLASS } from "./thumb-frame";
import { COLUMN_GAP, fitTiles, MIN_TILE_SIZE, ROW_GAP } from "./tile-size";

type AssetTileGridProps = {
  files: AssetFileRef[];
  isEditable: boolean;
  onPreview: (attachmentId: number) => void;
  onRemoveFile: (attachmentId: number) => void;
  onRemoveUpload?: (uploadId: string) => void;
  onRetryUpload?: (uploadId: string) => void;
  title: string;
  unfinishedUploads?: ManagedFile[];
};

function AddMoreTile() {
  return (
    <FileUploadTrigger>
      <Button
        aria-label="Add more files"
        className="flex aspect-square size-full cursor-pointer flex-col items-center justify-center gap-xs rounded-xl border-[1.5px] border-dashed border-border-primary bg-bg-primary text-xs font-semibold text-text-brand-primary outline-hidden transition-colors hover:border-border-brand-solid focus-visible:ring-2 focus-visible:ring-fg-brand-primary group-data-[drop-target]/panel:border-border-brand-solid"
        variant="unstyled"
      >
        <Plus className="size-5" />
        Add more
      </Button>
    </FileUploadTrigger>
  );
}

function HiddenFilesTile({
  files,
  onPress,
}: {
  files: AssetFileRef[];
  onPress: () => void;
}) {
  const cover = files.find((file) => (file.mime ?? "").startsWith("image/"));

  return (
    <button
      aria-label={`Show all ${files.length} files`}
      className="relative aspect-square size-full cursor-pointer outline-hidden focus-visible:ring-2 focus-visible:ring-fg-brand-primary"
      onClick={onPress}
      type="button"
    >
      <span className="absolute inset-0 translate-x-1.5 translate-y-1.5 rounded-xl border border-border-secondary bg-bg-tertiary" />
      <span className="absolute inset-0 translate-x-0.5 translate-y-0.5 rounded-xl border border-border-secondary bg-bg-secondary" />
      <span className="absolute inset-0 flex items-center justify-center overflow-hidden rounded-xl border border-border-secondary bg-bg-secondary">
        {cover ? (
          <img
            alt=""
            className="absolute inset-0 size-full object-cover opacity-40 blur-sm"
            src={cover.url}
          />
        ) : null}
        <span className="relative text-lg font-semibold text-text-primary">
          +{files.length}
        </span>
      </span>
    </button>
  );
}

function UnfinishedTile({
  file,
  onRemove,
  onRetry,
}: {
  file: ManagedFile;
  onRemove?: (uploadId: string) => void;
  onRetry?: (uploadId: string) => void;
}) {
  const name = getFileName(file);

  if (file.status !== "error") {
    return (
      <div className="min-w-0" title={name}>
        <div className={THUMB_FRAME_CLASS}>
          <div className="size-full opacity-55">
            <DocPlaceholder name={name} />
          </div>
          <div
            aria-label={`Uploading ${name}`}
            aria-valuemax={100}
            aria-valuemin={0}
            aria-valuenow={file.progress}
            className="absolute inset-x-0 bottom-0 h-1 bg-bg-primary-solid/25"
            role="progressbar"
          >
            <div
              className="h-full bg-bg-brand-solid transition-[width] duration-75 ease-linear"
              style={{ width: `${file.progress}%` }}
            />
          </div>
        </div>
        <p className="mt-md truncate text-[13px] font-medium text-text-primary">
          {name}
        </p>
        <p className="text-xs text-text-quaternary">Uploading</p>
      </div>
    );
  }

  return (
    <div className="min-w-0" title={name}>
      <div
        className={`${THUMB_FRAME_CLASS} flex items-center justify-center border-border-error bg-bg-error-primary`}
      >
        <div className="flex gap-md">
          {file.isRetryable === false ? null : (
            <button
              aria-label={`Retry ${name}`}
              className="grid size-8 cursor-pointer place-items-center rounded-full bg-bg-primary text-text-tertiary shadow-xs outline-hidden hover:text-text-primary focus-visible:ring-2 focus-visible:ring-fg-brand-primary"
              onClick={() => onRetry?.(file.id)}
              type="button"
            >
              <RotateCw className="size-4" />
            </button>
          )}
          <button
            aria-label={`Remove ${name}`}
            className="grid size-8 cursor-pointer place-items-center rounded-full bg-bg-primary text-text-tertiary shadow-xs outline-hidden hover:text-text-error-primary focus-visible:ring-2 focus-visible:ring-fg-brand-primary"
            onClick={() => onRemove?.(file.id)}
            type="button"
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      </div>
      <p className="mt-md truncate text-[13px] font-medium text-text-primary">
        {name}
      </p>
      <p className="truncate text-xs text-text-error-primary">
        {file.errorMessage ?? "Upload failed"}
      </p>
    </div>
  );
}

export function AssetTileGrid({
  files,
  isEditable,
  onPreview,
  onRemoveFile,
  onRemoveUpload,
  onRetryUpload,
  title,
  unfinishedUploads = [],
}: AssetTileGridProps) {
  const [grid, setGrid] = useState<HTMLDivElement | null>(null);
  const [isAllFilesOpen, setIsAllFilesOpen] = useState(false);
  const width = useElementWidth(grid);

  const reservedTiles = unfinishedUploads.length + (isEditable ? 1 : 0);
  const tileCount = reservedTiles + files.length;
  const isMeasured = width > 0;
  const fit = fitTiles({ tileCount, width });

  const visibleFileCount =
    isMeasured && fit.grouped
      ? Math.max(0, fit.capacity - 1 - reservedTiles)
      : files.length;
  const visibleFiles = files.slice(0, visibleFileCount);
  const hiddenFiles = files.slice(visibleFileCount);

  if (tileCount === 0) return null;

  return (
    <div
      className="grid"
      ref={setGrid}
      style={{
        columnGap: COLUMN_GAP,
        gridTemplateColumns: isMeasured
          ? `repeat(${fit.columns}, minmax(0, 1fr))`
          : `repeat(auto-fill, minmax(${MIN_TILE_SIZE}px, 1fr))`,
        rowGap: ROW_GAP,
      }}
    >
      {visibleFiles.map((file) => (
        <AssetFileTile
          file={file}
          key={file.attachmentId}
          onPreview={onPreview}
          onRemove={isEditable ? onRemoveFile : undefined}
        />
      ))}
      {hiddenFiles.length > 0 ? (
        <>
          <HiddenFilesTile
            files={hiddenFiles}
            onPress={() => setIsAllFilesOpen(true)}
          />
          <AllFilesModal
            files={files}
            isOpen={isAllFilesOpen}
            onOpenChange={setIsAllFilesOpen}
            onPreview={onPreview}
            onRemoveFile={isEditable ? onRemoveFile : undefined}
            title={title}
          />
        </>
      ) : null}
      {unfinishedUploads.map((file) => (
        <UnfinishedTile
          file={file}
          key={file.id}
          onRemove={onRemoveUpload}
          onRetry={onRetryUpload}
        />
      ))}
      {isEditable ? <AddMoreTile /> : null}
    </div>
  );
}
